import { OverlayToaster, Position, ToasterInstance } from '@blueprintjs/core';
import React, { createContext, ReactNode, useContext, useRef } from 'react';

interface ToasterContextType {
  toaster: ToasterInstance | null;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showWarning: (message: string) => void;
}

const ToasterContext = createContext<ToasterContextType | undefined>(undefined);

interface ToasterProviderProps {
  children: ReactNode;
}

export const ToasterProvider = ({ children }: ToasterProviderProps): React.ReactElement => {
  const toasterRef = useRef<OverlayToaster>(null);

  const showError = (message: string) => {
    toasterRef.current?.show({
      message,
      intent: "danger",
      icon: "error"
    });
  };

  const showSuccess = (message: string) => {
    toasterRef.current?.show({
      message,
      intent: "success",
      icon: "tick"
    });
  };

  const showWarning = (message: string) => {
    toasterRef.current?.show({
      message,
      intent: "warning",
      icon: "warning-sign"
    });
  };

  return (
    <ToasterContext.Provider value={{
      toaster: toasterRef.current,
      showError,
      showSuccess,
      showWarning
    }}>
      <OverlayToaster ref={toasterRef} position={Position.TOP} />
      {children}
    </ToasterContext.Provider>
  );
};

export const useToaster = (): ToasterContextType => {
  const context = useContext(ToasterContext);
  if (context === undefined) {
    throw new Error('useToaster must be used within a ToasterProvider');
  }
  return context;
}; 